import { useEffect, useState } from 'react';
import { useGetLoan } from '@repo/api/endpoints/loan';
import type { MerchantResponse } from '@repo/api/endpoints/merchant';
import { loanProviderRequest, loanResponseStatus, providerType } from '@repo/shared/types';
import { env } from '@repo/env';
import { loanSuccessResponseBodyStage7 } from '@repo/api/endpoints/loan';
import { useProviderStore } from '@repo/store/provider';

import { ApplicationSuspended } from '@/components/loan-process/loan-stages/shared/application-suspended/application-suspended';
import { ApplicationProcessing } from '@/components/loan-process/loan-stages/stage-0/application-processing/application-processing';
import {
  type LoanStoreData,
  useLoanProcessStore,
} from '@/components/loan-process/store/loan-process.store';
import { useLoanProcessModal } from '@/components/loan-process/hooks/use-loan-process-modal';
import {
  isLoanPositiveResponse,
  isLoanPositiveResponseStage1,
  isLoanPositiveResponseStage7,
} from '@/components/loan-process/loan-stages/shared/is-loan-positive-response';
import { useRefetchMerchantOnStageChange } from '@/components/loan-process/hooks/use-refetch-merchant-on-stage-change';

interface StageOne {
  merchantResponseData: MerchantResponse | undefined;
  loanData: LoanStoreData | undefined;
}

export function StageOne({ merchantResponseData, loanData }: StageOne) {
  const [isGetLoanPollingEnabled, setIsGetLoanPollingEnabled] = useState(false);

  const {
    data: singleLoanData,
    error: singleLoanError,
    refetch: getSingleLoan,
    isFetching: isSingleLoanFetching,
  } = useGetLoan(isGetLoanPollingEnabled);

  const {
    actions: { setLoanData, setLoanLimit, setLoanRejectionDescription },
  } = useLoanProcessStore();

  const {
    state: { currentProvider },
  } = useProviderStore();

  useRefetchMerchantOnStageChange();

  const { setLoanStage } = useLoanProcessModal();

  const isApplicationPositive =
    isLoanPositiveResponse(singleLoanData) &&
    singleLoanData.data.status === loanResponseStatus.enum.AGREED &&
    singleLoanData.data.stage === 2 &&
    singleLoanData.data.loan.providerRequest === loanProviderRequest.enum.gotNotification;

  const isApplicationFalsyPositive =
    isLoanPositiveResponse(singleLoanData) &&
    singleLoanData.data.status === loanResponseStatus.enum.AGREED &&
    singleLoanData.data.stage === 2 &&
    singleLoanData.data.loan.providerRequest !== loanProviderRequest.enum.gotNotification;

  const isProviderRequestFailed =
    (isLoanPositiveResponse(singleLoanData) &&
      singleLoanData.data.loan.providerRequest === loanProviderRequest.enum.requestFailed) ||
    (isLoanPositiveResponseStage1(singleLoanData) &&
      singleLoanData.data.providerRequest === loanProviderRequest.enum.requestFailed) ||
    (isLoanPositiveResponseStage7(singleLoanData) &&
      singleLoanData.data.providerRequest === loanProviderRequest.enum.requestFailed);

  const isApplicationNegative =
    singleLoanError ||
    singleLoanData?.data.status === loanResponseStatus.enum.REJECTED ||
    isProviderRequestFailed;

  const isLoanPoolingToSkip =
    isLoanPositiveResponse(singleLoanData) && singleLoanData.data.stage > 2;

  const isApplicationProcessing = isSingleLoanFetching || isGetLoanPollingEnabled;

  useEffect(() => {
    if (merchantResponseData && loanData && currentProvider) {
      setIsGetLoanPollingEnabled(true);
      getSingleLoan({
        loanId: loanData.id.toString(),
        provider: providerType.enum.pragma,
        requestId: merchantResponseData.requestId,
      });
    }
  }, [merchantResponseData, loanData, currentProvider]);

  useEffect(() => {
    if (isApplicationPositive) {
      setLoanData({
        amount: singleLoanData.data.loan.amount,
        id: singleLoanData.data.loan.id,
        period: singleLoanData.data.loan.period,
        pricing: singleLoanData.data.loan.pricing,
        loanRepaymentPrice: singleLoanData.data.loan.loanRepaymentPrice,
        documents: singleLoanData.data.documents || [],
        loanAmountChange: singleLoanData.data.loan.loanAmountChange,
        provider: singleLoanData.data.loan.provider,
      });
      setLoanLimit(singleLoanData.data.limit);
      setLoanStage(2, {
        viewOptions: {
          stageViewType: 'APPLICATION_POSITIVE',
        },
      });
    }
  }, [singleLoanData, isApplicationPositive]);

  useEffect(() => {
    const parsedResponse = loanSuccessResponseBodyStage7.safeParse(singleLoanData);
    if (
      isApplicationNegative &&
      parsedResponse.success &&
      parsedResponse.data.data.rejectionDescription
    ) {
      setLoanRejectionDescription(parsedResponse.data.data.rejectionDescription);
      setLoanStage(7);
    }
  }, [singleLoanData]);

  useEffect(() => {
    if (
      isApplicationPositive ||
      isApplicationFalsyPositive ||
      isApplicationNegative ||
      isLoanPoolingToSkip
    ) {
      setIsGetLoanPollingEnabled(false);
    }
  }, [
    isApplicationFalsyPositive,
    isApplicationNegative,
    isApplicationPositive,
    isLoanPoolingToSkip,
  ]);

  const pollingTimeout = env.VITE_POLLING_TIMEOUT ? parseInt(env.VITE_POLLING_TIMEOUT, 10) : 600000;
  useEffect(() => {
    const pollingTimeoutTimer = setTimeout(() => {
      setIsGetLoanPollingEnabled(false);
    }, pollingTimeout);

    return () => {
      clearTimeout(pollingTimeoutTimer);
    };
  }, []);

  useEffect(() => {
    return () => {
      setIsGetLoanPollingEnabled(false);
    };
  }, []);

  if (isApplicationProcessing) {
    return <ApplicationProcessing />;
  }

  return <ApplicationSuspended />;
}
